import { Link, useNavigate } from 'react-router-dom'
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from 'lucide-react'
import { useCartStore } from '@/stores/cartStore'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { useState } from 'react'
import api from '@/services/api'
import { getErrorMessage } from '@/services/api'

export default function Cart() {
  const navigate = useNavigate()
  const {
    items,
    discountCode,
    removeItem,
    updateQuantity,
    clearCart,
    applyDiscountCode,
    clearDiscount,
    getSubtotal,
    getDiscountAmount,
    getTotal,
    getItemCount,
  } = useCartStore()

  const [discountInput, setDiscountInput] = useState('')
  const [discountError, setDiscountError] = useState('')
  const [discountLoading, setDiscountLoading] = useState(false)


  const handleApplyDiscount = async () => {
    if (!discountInput.trim()) {
      setDiscountError('Please enter a discount code')
      return
    }

    setDiscountLoading(true)
    setDiscountError('')

    try {
      const response = await api.post('/discounts/validate', {
        code: discountInput.trim(),
        cart_total: getSubtotal(),
      })

      if (response.data.success) {
        const { discount_amount, discount_percentage } = response.data
        applyDiscountCode(discountInput.trim(), discount_amount || 0, discount_percentage || 0)
        setDiscountInput('')
      }
    } catch (error) {
      setDiscountError(getErrorMessage(error))
    } finally {
      setDiscountLoading(false)
    }
  }

  const handleRemoveDiscount = () => {
    clearDiscount()
    setDiscountInput('')
    setDiscountError('')
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-16">
            <ShoppingBag className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Your cart is empty</h2>
            <p className="text-gray-600 mb-6">
              Looks like you haven't added any products to your cart yet.
            </p>
            <Link to="/shop">
              <Button>
                Continue Shopping
              </Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Shopping Cart</h1>
          <p className="mt-2 text-gray-600">
            {getItemCount()} {getItemCount() === 1 ? 'item' : 'items'} in your cart
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {items.map((item) => (
              <div
                key={item.product.id}
                className="bg-white rounded-lg shadow-sm p-4 sm:p-6"
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  {/* Product Image */}
                  {item.product.image_url && (
                    <img
                      src={item.product.image_url}
                      alt={item.product.name}
                      className="w-16 h-16 sm:w-24 sm:h-24 object-cover rounded-lg flex-shrink-0"
                    />
                  )}

                  {/* Product Details */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                      {item.product.name}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-600 mt-1 line-clamp-2">
                      {item.product.short_description}
                    </p>
                    {item.product.is_contract && (
                      <span className="inline-block mt-2 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                        Contract: {item.product.contract_months === 1 ? "Rolling monthly (30 days' notice)" : `${item.product.contract_months} months`}
                      </span>
                    )}
                    <div className="mt-2 text-base sm:text-lg font-bold text-gray-900">
                      £{(item.product.discounted_price || item.product.price).toFixed(2)}
                      {item.product.is_contract && <span className="text-sm font-normal text-gray-600">/month</span>}
                    </div>
                  </div>

                  {/* Remove Button (top right on mobile) */}
                  <button
                    onClick={() => removeItem(item.product.id)}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded-full transition-colors flex-shrink-0"
                    title="Remove from cart"
                  >
                    <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>

                {/* Quantity Controls (below on mobile) */}
                <div className="flex items-center gap-3 mt-3 sm:mt-0 sm:absolute sm:hidden">
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-2 sm:gap-3 bg-gray-100 rounded-full px-2 py-1">
                    <button
                      onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                      className="p-1 rounded-full hover:bg-gray-200 transition-colors"
                    >
                      <Minus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    </button>
                    <span className="w-6 text-center font-medium text-sm">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                      className="p-1 rounded-full hover:bg-gray-200 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    </button>
                  </div>
                  <span className="text-sm text-gray-500">
                    Total: £{((item.product.discounted_price || item.product.price) * item.quantity).toFixed(2)}
                  </span>
                </div>
              </div>
            ))}

            {/* Clear Cart */}
            <button
              onClick={clearCart}
              className="text-sm text-red-600 hover:text-red-800 font-medium"
            >
              Clear entire cart
            </button>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-4">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Order Summary</h2>

              {/* Discount Code */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Discount Code
                </label>
                {discountCode ? (
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-green-800">{discountCode}</p>
                      <p className="text-xs text-green-600">Discount applied</p>
                    </div>
                    <button
                      onClick={handleRemoveDiscount}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        value={discountInput}
                        onChange={(e) => setDiscountInput(e.target.value)}
                        placeholder="Enter code"
                        className="flex-1"
                        onKeyPress={(e) => e.key === 'Enter' && handleApplyDiscount()}
                      />
                      <Button
                        onClick={handleApplyDiscount}
                        disabled={discountLoading}
                        variant="outline"
                      >
                        {discountLoading ? 'Applying...' : 'Apply'}
                      </Button>
                    </div>
                    {discountError && (
                      <p className="text-sm text-red-600">{discountError}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Price Breakdown */}
              <div className="space-y-3 border-t border-gray-200 pt-4">
                <div className="flex justify-between text-gray-700">
                  <span>Subtotal</span>
                  <span>£{getSubtotal().toFixed(2)}</span>
                </div>

                {getDiscountAmount() > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount</span>
                    <span>-£{getDiscountAmount().toFixed(2)}</span>
                  </div>
                )}

                <div className="flex justify-between text-sm text-gray-600">
                  <span>Tax (VAT 20%)</span>
                  <span>Calculated at checkout</span>
                </div>

                <div className="flex justify-between text-sm text-gray-600">
                  <span>Delivery</span>
                  <span>Calculated at checkout</span>
                </div>

                <div className="border-t border-gray-200 pt-3 mt-3">
                  <div className="flex justify-between text-lg font-bold text-gray-900">
                    <span>Estimated Total</span>
                    <span>£{getTotal().toFixed(2)}</span>
                  </div>
                </div>
              </div>


              {/* Checkout Button */}
              <Button
                onClick={() => navigate('/checkout')}
                className="w-full mt-6"
                size="lg"
              >
                Proceed to Checkout
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>

              <Link
                to="/shop"
                className="block text-center text-sm text-blue-600 hover:text-blue-800 mt-4"
              >
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
